import React, { useState, useCallback } from 'react';
import { Play, Save, Download, Upload, Plus, Trash2, Copy, Settings, Zap, Mail, Phone, Clock, GitBranch, Filter, Database, MessageSquare, User } from 'lucide-react';

const WorkflowBuilder = () => {
  const [workflows, setWorkflows] = useState<any[]>([]);
  const [currentWorkflow, setCurrentWorkflow] = useState<any>(null);
  const [nodes, setNodes] = useState<any[]>([]);
  const [selectedNode, setSelectedNode] = useState<any>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [draggedNodeType, setDraggedNodeType] = useState<any>(null);

  // Node type definitions
  const nodeTypes = {
    trigger: {
      icon: Zap,
      label: 'Trigger',
      color: 'bg-yellow-500',
      options: [
        { id: 'new_lead', name: 'New Lead', description: 'When a new lead is created' },
        { id: 'form_submit', name: 'Form Submit', description: 'When a form is submitted' },
        { id: 'call_completed', name: 'Call Completed', description: 'After AI agent call ends' },
        { id: 'email_opened', name: 'Email Opened', description: 'When recipient opens email' },
        { id: 'schedule', name: 'Schedule', description: 'At specific time/date' }
      ]
    },
    action: {
      icon: Play,
      label: 'Action',
      color: 'bg-blue-500',
      options: [
        { id: 'send_email', name: 'Send Email', description: 'Send automated email' },
        { id: 'send_sms', name: 'Send SMS', description: 'Send text message' },
        { id: 'make_call', name: 'Make Call', description: 'Trigger AI agent call' },
        { id: 'update_lead', name: 'Update Lead', description: 'Update lead status/data' },
        { id: 'add_tag', name: 'Add Tag', description: 'Tag lead with label' },
        { id: 'assign_agent', name: 'Assign Agent', description: 'Assign to AI or human agent' }
      ]
    },
    condition: {
      icon: GitBranch,
      label: 'Condition',
      color: 'bg-purple-500',
      options: [
        { id: 'if_then', name: 'If/Then', description: 'Branch based on condition' },
        { id: 'ab_test', name: 'A/B Test', description: 'Split traffic for testing' },
        { id: 'score_check', name: 'Score Check', description: 'Check lead score threshold' },
        { id: 'time_check', name: 'Time Check', description: 'Check time of day/week' }
      ]
    },
    delay: {
      icon: Clock,
      label: 'Delay',
      color: 'bg-orange-500',
      options: [
        { id: 'wait', name: 'Wait', description: 'Wait for specified time' },
        { id: 'wait_until', name: 'Wait Until', description: 'Wait until condition met' }
      ]
    },
    filter: {
      icon: Filter,
      label: 'Filter',
      color: 'bg-green-500',
      options: [
        { id: 'segment', name: 'Segment', description: 'Filter by criteria' },
        { id: 'exclude', name: 'Exclude', description: 'Remove matching leads' }
      ]
    }
  };

  // Sample templates
  const templates = [
    {
      id: 'welcome_sequence',
      name: 'Welcome Sequence',
      description: 'Automated welcome series for new leads',
      nodes: [
        { id: '1', type: 'trigger', subtype: 'new_lead', label: 'New Lead Created', x: 50, y: 100 },
        { id: '2', type: 'delay', subtype: 'wait', label: 'Wait 5 minutes', config: { duration: 5, unit: 'minutes' }, x: 50, y: 200 },
        { id: '3', type: 'action', subtype: 'send_email', label: 'Send Welcome Email', x: 50, y: 300 },
        { id: '4', type: 'delay', subtype: 'wait', label: 'Wait 1 day', config: { duration: 1, unit: 'days' }, x: 50, y: 400 },
        { id: '5', type: 'action', subtype: 'make_call', label: 'AI Agent Follow-Up', x: 50, y: 500 }
      ]
    },
    {
      id: 'nurture_campaign',
      name: 'Nurture Campaign',
      description: 'Long-term lead nurturing with score tracking',
      nodes: [
        { id: '1', type: 'trigger', subtype: 'new_lead', label: 'New Lead', x: 50, y: 100 },
        { id: '2', type: 'filter', subtype: 'segment', label: 'Check Lead Score', x: 50, y: 200 },
        { id: '3', type: 'condition', subtype: 'if_then', label: 'Score > 50?', x: 50, y: 300 },
        { id: '4', type: 'action', subtype: 'assign_agent', label: 'Hot Lead: Assign Human', x: 200, y: 400 },
        { id: '5', type: 'action', subtype: 'send_email', label: 'Cold Lead: Nurture Email', x: -100, y: 400 }
      ]
    },
    {
      id: 'abandoned_quote',
      name: 'Abandoned Quote Recovery',
      description: 'Re-engage leads who started but didn\'t complete quote',
      nodes: [
        { id: '1', type: 'trigger', subtype: 'form_submit', label: 'Quote Started', x: 50, y: 100 },
        { id: '2', type: 'delay', subtype: 'wait', label: 'Wait 30 minutes', x: 50, y: 200 },
        { id: '3', type: 'condition', subtype: 'if_then', label: 'Quote Completed?', x: 50, y: 300 },
        { id: '4', type: 'action', subtype: 'send_sms', label: 'Send Reminder SMS', x: 200, y: 400 },
        { id: '5', type: 'delay', subtype: 'wait', label: 'Wait 24 hours', x: 200, y: 500 },
        { id: '6', type: 'action', subtype: 'make_call', label: 'AI Follow-Up Call', x: 200, y: 600 }
      ]
    }
  ];

  // Create new workflow
  const createWorkflow = () => {
    const newWorkflow = {
      id: Date.now().toString(),
      name: 'New Workflow',
      description: '',
      status: 'draft',
      nodes: [],
      createdAt: new Date().toISOString()
    };
    setWorkflows([...workflows, newWorkflow]);
    setCurrentWorkflow(newWorkflow);
    setNodes([]);
  };

  // Load template
  const loadTemplate = (template: any) => {
    setCurrentWorkflow({
      id: Date.now().toString(),
      name: template.name,
      description: template.description,
      status: 'draft',
      nodes: template.nodes,
      createdAt: new Date().toISOString()
    });
    setNodes(template.nodes);
  };

  // Add node to canvas
  const addNode = (type: string, subtype: string, x: number, y: number) => {
    const nodeTypeConfig = (nodeTypes as any)[type];
    const option = (nodeTypeConfig as any).options.find((opt: any) => opt.id === subtype);
    
    const newNode = {
      id: Date.now().toString(),
      type,
      subtype,
      label: option.name,
      description: option.description,
      x: x || 100,
      y: y || 100,
      config: {}
    };
    
    setNodes([...nodes, newNode]);
  };

  // Handle drag start
  const handleDragStart = (type: string, subtype: string) => {
    setIsDragging(true);
    setDraggedNodeType({ type, subtype });
  };

  // Handle drop
  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    if (draggedNodeType) {
      const rect = e.currentTarget.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;
      addNode(draggedNodeType.type, draggedNodeType.subtype, x, y);
    }
    setIsDragging(false);
    setDraggedNodeType(null);
  };

  // Save workflow
  const saveWorkflow = async () => {
    if (!currentWorkflow) return;
    
    const workflowData = {
      ...currentWorkflow,
      nodes,
      updatedAt: new Date().toISOString()
    };

    try {
      const response = await fetch('/api/workflows/save', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(workflowData)
      });
      
      if (response.ok) {
        alert('Workflow saved successfully!');
      }
    } catch (error) {
      console.error('Save failed:', error);
      alert('Failed to save workflow');
    }
  };

  // Test workflow
  const testWorkflow = async () => {
    if (!currentWorkflow || nodes.length === 0) {
      alert('Please add nodes to test workflow');
      return;
    }

    try {
      const response = await fetch('/api/workflows/test', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ nodes })
      });
      
      const result = await response.json();
      alert(`Test Result: ${result.success ? 'PASSED' : 'FAILED'}\n${result.message}`);
    } catch (error) {
      console.error('Test failed:', error);
      alert('Test failed');
    }
  };

  // Deploy workflow
  const deployWorkflow = async () => {
    if (!currentWorkflow) return;

    if (window.confirm('Deploy this workflow to production?')) {
      try {
        const response = await fetch('/api/workflows/deploy', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ workflowId: currentWorkflow.id })
        });
        
        if (response.ok) {
          alert('Workflow deployed successfully!');
          setCurrentWorkflow({ ...currentWorkflow, status: 'active' });
        }
      } catch (error) {
        console.error('Deploy failed:', error);
        alert('Deployment failed');
      }
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <h1 className="text-2xl font-bold text-gray-900">Workflow Builder</h1>
            {currentWorkflow && (
              <input
                type="text"
                value={currentWorkflow.name}
                onChange={(e) => setCurrentWorkflow({ ...currentWorkflow, name: e.target.value })}
                className="px-3 py-1 border border-gray-300 rounded-lg text-lg font-medium"
              />
            )}
          </div>
          
          <div className="flex items-center gap-2">
            <button
              onClick={testWorkflow}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
            >
              <Play className="w-4 h-4" />
              Test
            </button>
            <button
              onClick={saveWorkflow}
              className="px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 flex items-center gap-2"
            >
              <Save className="w-4 h-4" />
              Save
            </button>
            <button
              onClick={deployWorkflow}
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center gap-2"
            >
              <Zap className="w-4 h-4" />
              Deploy
            </button>
          </div>
        </div>
      </div>

      <div className="flex h-[calc(100vh-73px)]">
        {/* Left Sidebar - Node Palette */}
        <div className="w-64 bg-white border-r border-gray-200 overflow-y-auto">
          <div className="p-4">
            <h2 className="text-sm font-semibold text-gray-700 mb-4">BUILDING BLOCKS</h2>
            
            {Object.entries(nodeTypes).map(([type, config]) => {
              const Icon = config.icon;
              return (
                <div key={type} className="mb-6">
                  <div className="flex items-center gap-2 mb-2">
                    <div className={`${config.color} w-2 h-2 rounded-full`}></div>
                    <h3 className="text-xs font-semibold text-gray-600 uppercase">{config.label}</h3>
                  </div>
                  
                  <div className="space-y-2">
                    {config.options.map(option => (
                      <div
                        key={option.id}
                        draggable
                        onDragStart={() => handleDragStart(type, option.id)}
                        className="p-3 bg-gray-50 border border-gray-200 rounded-lg cursor-move hover:bg-gray-100 hover:border-gray-300 transition"
                      >
                        <div className="flex items-start gap-2">
                          <Icon className="w-4 h-4 text-gray-600 mt-0.5 flex-shrink-0" />
                          <div>
                            <div className="text-sm font-medium text-gray-900">{option.name}</div>
                            <div className="text-xs text-gray-500">{option.description}</div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Main Canvas */}
        <div className="flex-1 flex flex-col">
          {!currentWorkflow ? (
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center max-w-2xl px-4">
                <Zap className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                <h2 className="text-2xl font-bold text-gray-900 mb-2">Build Your First Workflow</h2>
                <p className="text-gray-600 mb-6">
                  Automate your insurance sales process with drag-and-drop workflows
                </p>
                
                <div className="flex items-center justify-center gap-4 mb-8">
                  <button
                    onClick={createWorkflow}
                    className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
                  >
                    <Plus className="w-5 h-5" />
                    Create Blank Workflow
                  </button>
                </div>

                <div className="text-left">
                  <h3 className="text-sm font-semibold text-gray-700 mb-3">OR START WITH A TEMPLATE:</h3>
                  <div className="grid gap-3">
                    {templates.map(template => (
                      <div
                        key={template.id}
                        onClick={() => loadTemplate(template)}
                        className="p-4 bg-white border border-gray-200 rounded-lg hover:border-blue-500 hover:shadow-md cursor-pointer transition"
                      >
                        <div className="font-medium text-gray-900 mb-1">{template.name}</div>
                        <div className="text-sm text-gray-600">{template.description}</div>
                        <div className="text-xs text-gray-500 mt-2">{template.nodes.length} steps</div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <>
              {/* Canvas Toolbar */}
              <div className="bg-white border-b border-gray-200 px-4 py-2 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-600">{nodes.length} nodes</span>
                  <span className="text-gray-300">|</span>
                  <span className={`text-sm px-2 py-1 rounded ${
                    currentWorkflow.status === 'active' 
                      ? 'bg-green-100 text-green-700' 
                      : 'bg-gray-100 text-gray-700'
                  }`}>
                    {currentWorkflow.status}
                  </span>
                </div>
                
                <div className="flex items-center gap-2">
                  <button className="p-2 hover:bg-gray-100 rounded">
                    <Download className="w-4 h-4 text-gray-600" />
                  </button>
                  <button className="p-2 hover:bg-gray-100 rounded">
                    <Upload className="w-4 h-4 text-gray-600" />
                  </button>
                  <button className="p-2 hover:bg-gray-100 rounded">
                    <Copy className="w-4 h-4 text-gray-600" />
                  </button>
                  <button className="p-2 hover:bg-gray-100 rounded">
                    <Trash2 className="w-4 h-4 text-gray-600" />
                  </button>
                </div>
              </div>

              {/* Canvas Area */}
              <div
                className="flex-1 bg-gray-100 overflow-auto relative"
                onDrop={handleDrop}
                onDragOver={(e) => e.preventDefault()}
              >
                {/* Grid pattern */}
                <div className="absolute inset-0" style={{
                  backgroundImage: 'radial-gradient(circle, #d1d5db 1px, transparent 1px)',
                  backgroundSize: '20px 20px'
                }}></div>

                {/* Nodes */}
                <div className="relative">
                  {nodes.map((node, index) => {
                    const nodeTypeConfig = (nodeTypes as any)[node.type];
                    const Icon = nodeTypeConfig.icon;
                    
                    return (
                      <div
                        key={node.id}
                        onClick={() => setSelectedNode(node)}
                        className={`absolute bg-white border-2 rounded-lg shadow-lg p-4 cursor-pointer transition ${
                          selectedNode?.id === node.id 
                            ? 'border-blue-500 shadow-xl' 
                            : 'border-gray-300 hover:border-gray-400'
                        }`}
                        style={{
                          left: node.x,
                          top: node.y,
                          width: '200px',
                          zIndex: selectedNode?.id === node.id ? 10 : 1
                        }}
                      >
                        <div className="flex items-start gap-3">
                          <div className={`${nodeTypeConfig.color} p-2 rounded`}>
                            <Icon className="w-5 h-5 text-white" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="font-medium text-gray-900 text-sm">{node.label}</div>
                            <div className="text-xs text-gray-500 mt-1">{node.description}</div>
                          </div>
                        </div>
                        
                        {/* Connection dots */}
                        {index < nodes.length - 1 && (
                          <div className="absolute -bottom-3 left-1/2 transform -translate-x-1/2 w-6 h-6 bg-blue-500 rounded-full border-4 border-white flex items-center justify-center">
                            <div className="w-2 h-2 bg-white rounded-full"></div>
                          </div>
                        )}
                      </div>
                    );
                  })}

                  {/* Connection lines */}
                  <svg className="absolute inset-0 pointer-events-none" style={{ zIndex: 0 }}>
                    {nodes.map((node, index) => {
                      if (index < nodes.length - 1) {
                        const nextNode = nodes[index + 1];
                        return (
                          <line
                            key={`line-${node.id}`}
                            x1={node.x + 100}
                            y1={node.y + 80}
                            x2={nextNode.x + 100}
                            y2={nextNode.y}
                            stroke="#3b82f6"
                            strokeWidth="2"
                            strokeDasharray="5,5"
                          />
                        );
                      }
                      return null;
                    })}
                  </svg>

                  {nodes.length === 0 && (
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="text-center text-gray-500">
                        <Zap className="w-12 h-12 mx-auto mb-2 opacity-50" />
                        <p className="text-sm">Drag blocks from the left to start building</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </>
          )}
        </div>

        {/* Right Sidebar - Node Properties */}
        {selectedNode && (
          <div className="w-80 bg-white border-l border-gray-200 overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">Node Settings</h3>
                <button
                  onClick={() => setSelectedNode(null)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  ×
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Label</label>
                  <input
                    type="text"
                    value={selectedNode.label}
                    onChange={(e) => {
                      const updated = nodes.map(n => 
                        n.id === selectedNode.id ? { ...n, label: e.target.value } : n
                      );
                      setNodes(updated);
                      setSelectedNode({ ...selectedNode, label: e.target.value });
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Type</label>
                  <div className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm text-gray-700">
                    {(nodeTypes as any)[selectedNode.type].label}
                  </div>
                </div>

                {selectedNode.type === 'delay' && (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Duration</label>
                      <input
                        type="number"
                        defaultValue={selectedNode.config?.duration || 5}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Unit</label>
                      <select className="w-full px-3 py-2 border border-gray-300 rounded-lg">
                        <option>Minutes</option>
                        <option>Hours</option>
                        <option>Days</option>
                        <option>Weeks</option>
                      </select>
                    </div>
                  </>
                )}

                {selectedNode.type === 'action' && selectedNode.subtype === 'send_email' && (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Email Template</label>
                      <select className="w-full px-3 py-2 border border-gray-300 rounded-lg">
                        <option>Welcome Email</option>
                        <option>Quote Follow-Up</option>
                        <option>Policy Reminder</option>
                      </select>
                    </div>
                  </>
                )}

                {selectedNode.type === 'condition' && (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Condition</label>
                      <select className="w-full px-3 py-2 border border-gray-300 rounded-lg">
                        <option>Lead Score &gt; 50</option>
                        <option>Email Opened</option>
                        <option>Form Completed</option>
                        <option>Call Answered</option>
                      </select>
                    </div>
                  </>
                )}

                <button
                  onClick={() => {
                    setNodes(nodes.filter(n => n.id !== selectedNode.id));
                    setSelectedNode(null);
                  }}
                  className="w-full px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 flex items-center justify-center gap-2"
                >
                  <Trash2 className="w-4 h-4" />
                  Delete Node
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default WorkflowBuilder;